import * as React from 'react';
import type { WrappedFieldArrayProps } from 'redux-form';
import { Field } from 'redux-form';

import { filterMap } from '@acceligentllc/shared/utils/array';

import type { QuantityUnitType } from '@acceligentllc/shared/enums/quantityUnit';
import { PlainUnitEnum, QuantityUnitLabel, isQuantityUnit } from '@acceligentllc/shared/enums/quantityUnit';
import * as ReportBlockFieldEnum from '@acceligentllc/shared/enums/reportBlockField';

import { MAX_BILLABLE_WORK_DEFINITION_FIELDS } from 'ab-common/constants/value';

import { Button } from '@acceligentllc/storybook';

import Dropdown from 'af-fields/Dropdown';
import Input from 'af-fields/Input';

import type { FormModel } from '../FormModel';
import { renderEquipmentFieldFromJsonForm } from './helpers';
import styles from './styles.module.scss';

type OriginalUnits = {
	[key: string]: Nullable<string>;
};

type Props = WrappedFieldArrayProps<FormModel['definitionFields'][0]> & { originalUnits: OriginalUnits; };

interface DropdownOption<T> {
	id: T;
	label: string;
}

const compoundKeys = Object.keys(PlainUnitEnum) as PlainUnitEnum[];
const _quantityUnitOptions = filterMap(compoundKeys,
	(_unit) => !isQuantityUnit(QuantityUnitLabel[_unit]),
	(_unit): DropdownOption<QuantityUnitType> => ({
		id: _unit,
		label: QuantityUnitLabel[_unit],
	})
);
const quantityUnitOptions = [
	{ id: ' ', label: <span className={styles.dropdown_empty}>None</span> },
	..._quantityUnitOptions,
];

const AddBillableWorkModalDefinitionField: React.FC<Props> = (props) => {
	const { fields, originalUnits } = props;

	const onAddDefinitionFieldClick = React.useCallback(() => fields.push({ name: null, value: null, unit: null, type: null }), [fields]);
	const onRemoveDefinitionFieldClick = React.useCallback((index: number) => () => {
		const indexField = fields.getAll()[index];

		if (indexField.name) {
			fields.splice(index, 1, { name: indexField.name, value: null, unit: indexField.unit ? ' ' : null, type: indexField.type });
		} else {
			fields.remove(index);
		}
	}, [fields]);
	const filterUnits = React.useCallback((option: DropdownOption<QuantityUnitType>, searchText: string) => {
		return option.label?.toLowerCase()?.includes(searchText);
	}, []);
	const onUnitClear = React.useCallback((index: number) => () => {
		const indexField = fields.getAll()[index];
		fields.splice(index, 1, { ...indexField, unit: ' ' });
	}, [fields]);

	const fieldNames = React.useMemo(() => fields.getAll()?.map((_field) => _field?.name), [fields]);
	const fieldUnits = React.useMemo(() => fields.getAll()?.map((_field) => _field?.unit), [fields]);

	const formatField = React.useCallback((type: Nullable<string>) => {
		return (value) => {
			if (value && type === ReportBlockFieldEnum.Type.EQUIPMENT) {
				return renderEquipmentFieldFromJsonForm(value);
			}
			return value ?? '';
		};
	}, []);

	const renderField = (field: string, index: number) => {
		const type = fields.getAll()[index]?.type;
		return (
			<>
				<div>{fieldNames[index] ?? ''}</div>
				<div className={styles['add-billable-work-modal__info-def-item']} key={field}>
					<Field
						component={Input}
						format={formatField(type)}
						id="definitionField"
						label={`Definition Field ${index + 1}`}
						name={`${field}.value`}
						placeholder="Add definition field"
						type="text"
					/>
					{(Boolean(fieldUnits[index]) || Boolean(originalUnits[`d${index + 1}`]))
						&&
						<div className={styles['add-billable-work-modal__info-def-item__unit-field']}>
							<Field
								className={styles['unit-price']}
								component={Dropdown}
								defaultValue={null}
								disabled={false}
								filterable={true}
								filterBy={filterUnits}
								fixed={true}
								id="unit"
								label={'Unit'}
								labelKey="label"
								name={`${field}.unit`}
								onClear={onUnitClear(index)}
								options={quantityUnitOptions}
								placeholder="Add Unit"
								valueKey="id"
								withCaret={true}
							/>
						</div>
					}
					<Button
						icon="Delete"
						onClick={onRemoveDefinitionFieldClick(index)}
						style="link-danger"
					/>
				</div>
			</>
		);
	};
	return (
		<>
			{fields.map(renderField)}
			{fields.length < MAX_BILLABLE_WORK_DEFINITION_FIELDS &&
				<Button
					icon="ActionPlusSmall"
					label="Add Definition Field"
					onClick={onAddDefinitionFieldClick}
					style="link"
				/>
			}
		</>
	);
};

export default AddBillableWorkModalDefinitionField;
